import React, { useEffect } from 'react';
import SentryContextProvider from 'utils/Sentry';
import { MatomoProvider, createInstance } from '@jonkoops/matomo-tracker-react';
import 'styles/globals.scss';
import { ThemeProvider } from '@material-ui/core/styles';
import { Layout, AuthGuard } from 'components';
import store from 'store';
import { Provider } from 'react-redux';
import { ApolloProvider } from '@apollo/client';
import getConfig from 'next/config';
import { SSRKeycloakProvider } from '@react-keycloak-fork/ssr';
import cookie from 'cookie';
import { DefaultSeo } from 'next-seo';
import client from 'utils/apolloClient';
import { ModalProvider } from 'hooks/useModal';
import theme from '../src/theme';
import keycloakInit from '../src/utils/keycloak';
import SEO from '../next-seo.config';

function MyApp({ Component, pageProps, cookies }) {
    const config = getConfig();

    const version = process.env.REACT_APP_VERSION;

    console.log(`Penly frontend: version ${version}`); // eslint-disable-line no-console

    useEffect(() => {
        // Remove the server-side injected CSS.
        // see https://stackoverflow.com/questions/63521538/removing-server-side-injected-css
        const jssStyles = document.querySelector('#jss-server-side');
        if (jssStyles) jssStyles.parentElement.removeChild(jssStyles);
    }, []);

    useEffect(() => {
        // Add body attribute for iOS browsers
        if ((/iPad|iPhone|iPod/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) && !window.MSStream) {
            document.body.setAttribute('class', 'mobilesafari');
        }

        // Add body attribute for IE browsers
        if (window?.document?.documentMode) {
            document.body.setAttribute('class', 'deprecatedbrowser');
        }
    });

    const instance = createInstance({
        urlBase: config.publicRuntimeConfig.matomo.urlBase,
        siteId: config.publicRuntimeConfig.matomo.siteId,
        configurations: { requireCookieConsent: false },
    });

    const { keycloakConfig, persistor, initOptions } = keycloakInit({ cookies, config });

    return (
        <>
            <DefaultSeo { ...SEO } />
            <SentryContextProvider>
                <ApolloProvider client={ client }>
                    <MatomoProvider value={ instance }>
                        <Provider store={ store() }>
                            <ModalProvider>
                                <ThemeProvider theme={ theme }>
                                    <SSRKeycloakProvider keycloakConfig={ keycloakConfig } persistor={ persistor } initOptions={ initOptions }>
                                        <AuthGuard>
                                            <Layout>
                                                <Component { ...pageProps } />
                                            </Layout>
                                        </AuthGuard>
                                    </SSRKeycloakProvider>
                                </ThemeProvider>
                            </ModalProvider>
                        </Provider>
                    </MatomoProvider>
                </ApolloProvider>
            </SentryContextProvider>
        </>
    );
}

function parseCookies(req) {
    if (!req || !req.headers) {
        return {};
    }
    return cookie.parse(req.headers.cookie || '');
}

MyApp.getInitialProps = async context =>
    // Extract cookies from AppContext
    ({
        cookies: parseCookies(context?.ctx?.req),
    })
;
export default MyApp;
